import { Card, Form, Input, Button, Typography, App, Tabs, Divider } from 'antd';
import { trpc } from '../../lib/api';
import { PhoneInput, PostalCodeInput } from '../../components/common';

const { Title, Text } = Typography;

type ProfileFormValues = Record<string, unknown>;

interface PasswordFormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export function AgencyProfilePage() {
  const { message } = App.useApp();
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const utils = trpc.useUtils();

  // プロフィール取得
  const { data: profile, isLoading } = trpc.agencyProfile.getProfile.useQuery();

  // プロフィール更新
  const updateMutation = trpc.agencyProfile.updateProfile.useMutation({
    onSuccess: () => {
      message.success('プロフィールを更新しました');
      utils.agencyProfile.getProfile.invalidate();
    },
    onError: (error) => {
      message.error(error.message || 'プロフィールの更新に失敗しました');
    },
  });

  // パスワード変更
  const changePasswordMutation = trpc.agencyProfile.changePassword.useMutation({
    onSuccess: () => {
      message.success('パスワードを変更しました');
      passwordForm.resetFields();
    },
    onError: (error) => {
      message.error(error.message || 'パスワードの変更に失敗しました');
    },
  });

  const handleProfileUpdate = (values: ProfileFormValues) => {
    updateMutation.mutate(values);
  };

  const handlePasswordChange = (values: PasswordFormValues) => {
    changePasswordMutation.mutate(values);
  };

  const profileItems = [
    {
      key: 'basic',
      label: '基本情報',
      children: (
        <Form
          form={profileForm}
          layout="vertical"
          initialValues={profile}
          onFinish={handleProfileUpdate}
        >
          <Form.Item label="クーポンプレフィックス">
            <Input value={profile?.couponPrefix} disabled />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              ※クーポンプレフィックスは変更できません
            </Text>
          </Form.Item>

          <Form.Item
            name="name"
            label="代理店名"
            rules={[{ required: true, message: '代理店名を入力してください' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="companyName" label="会社名">
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label="メールアドレス"
          >
            <Input disabled />
          </Form.Item>

          <Form.Item
            name="phone"
            label="電話番号"
            rules={[{ required: true, message: '電話番号を入力してください' }]}
          >
            <PhoneInput />
          </Form.Item>

          <Divider />

          <Title level={5}>住所</Title>

          <Form.Item
            name="postalCode"
            label="郵便番号"
            rules={[{ required: true, message: '郵便番号を入力してください' }]}
          >
            <PostalCodeInput placeholder="100-0001" />
          </Form.Item>

          <Form.Item
            name="prefecture"
            label="都道府県"
            rules={[{ required: true, message: '都道府県を入力してください' }]}
          >
            <Input placeholder="東京都" />
          </Form.Item>

          <Form.Item
            name="city"
            label="市区町村"
            rules={[{ required: true, message: '市区町村を入力してください' }]}
          >
            <Input placeholder="千代田区" />
          </Form.Item>

          <Form.Item
            name="address"
            label="住所"
            rules={[{ required: true, message: '住所を入力してください' }]}
          >
            <Input placeholder="千代田1-1-1" />
          </Form.Item>

          <Form.Item name="building" label="建物名・部屋番号">
            <Input placeholder="千代田ビル3F" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={updateMutation.isPending}>
              更新
            </Button>
          </Form.Item>
        </Form>
      ),
    },
    {
      key: 'bank',
      label: '支払先情報',
      children: (
        <Form
          form={profileForm}
          layout="vertical"
          initialValues={profile}
          onFinish={handleProfileUpdate}
        >
          <Form.Item
            name="bankName"
            label="銀行名"
            rules={[{ required: true, message: '銀行名を入力してください' }]}
          >
            <Input placeholder="XXX銀行" />
          </Form.Item>

          <Form.Item
            name="bankBranchName"
            label="支店名"
            rules={[{ required: true, message: '支店名を入力してください' }]}
          >
            <Input placeholder="本店" />
          </Form.Item>

          <Form.Item
            name="bankAccountType"
            label="口座種別"
            rules={[{ required: true, message: '口座種別を選択してください' }]}
          >
            <Input placeholder="普通 または 当座" />
          </Form.Item>

          <Form.Item
            name="bankAccountNumber"
            label="口座番号"
            rules={[{ required: true, message: '口座番号を入力してください' }]}
          >
            <Input placeholder="1234567" />
          </Form.Item>

          <Form.Item
            name="bankAccountHolder"
            label="口座名義"
            rules={[{ required: true, message: '口座名義を入力してください' }]}
          >
            <Input placeholder="カブシキガイシャXXX" />
          </Form.Item>

          <Form.Item name="invoiceRegistrationNumber" label="適格請求書発行事業者登録番号">
            <Input placeholder="T1234567890123" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={updateMutation.isPending}>
              更新
            </Button>
          </Form.Item>
        </Form>
      ),
    },
    {
      key: 'password',
      label: 'パスワード変更',
      children: (
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handlePasswordChange}
        >
          <Form.Item
            name="currentPassword"
            label="現在のパスワード"
            rules={[{ required: true, message: '現在のパスワードを入力してください' }]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="新しいパスワード"
            rules={[
              { required: true, message: '新しいパスワードを入力してください' },
              { min: 8, message: 'パスワードは8文字以上必要です' },
            ]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="新しいパスワード（確認）"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'パスワードを再入力してください' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('パスワードが一致しません'));
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={changePasswordMutation.isPending}>
              パスワードを変更
            </Button>
          </Form.Item>
        </Form>
      ),
    },
  ];

  return (
    <div>
      <Title level={2}>プロフィール管理</Title>

      <Card loading={isLoading}>
        <Tabs items={profileItems} />
      </Card>
    </div>
  );
}
